import {Injectable, Injector} from '@angular/core';
import {L10nTranslationService} from 'angular-l10n';
import * as _ from 'lodash-es';
import {nanoid} from 'nanoid';
import {AsyncSubject, merge, NEVER, Observable, ReplaySubject, Subject, Subscription} from 'rxjs';
import {filter, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';
import {dependOn} from '../../../common/rxjs/depend-on';
import {WidgetLinkingGroupId} from '../../../enums/widget-linking-group';
import {WidgetActionEvent} from '../../../pages/dashboard/layout/widget';
import {getWidgetSettingsWithUpdatesStream} from '../../../widgets/components/common-widget.implementation';
import {GROUP_SETTINGS_UPDATE_FLAG, WidgetLinkingGroupService} from '../../widget-linking-group';
import {Cell} from '../cell';
import {SizesStandard} from '../sizes-standard';
import {WidgetDescription, WidgetSettingsSchemaField} from './widget-description';
import {WidgetImplementation, WidgetImplementationEvent, WidgetImplementationEventType} from './widget-implementation';
import {
    WidgetLinkingGroupIdNotification,
    WidgetNotification,
    WidgetNotificationType,
    WidgetSettingsNotification
} from './widget-notification';
import {WidgetSettings} from './widget-settings';

interface SettingsMap {
    [name: string]: string;
}

export type WidgetId = string;

@Injectable()
export class Widget {

    public static getNewWidgetId(): WidgetId {
        return nanoid();
    }

    public id: WidgetId;

    public cell: Cell;

    public tabId: string;

    public implementationInstance: WidgetImplementation;

    public widgetDescription: WidgetDescription;

    public destroy$: Observable<any>;

    public linkingGroupId: WidgetLinkingGroupId;

    public settingsWithUpdates$: Observable<Partial<WidgetSettings>>;

    public linkingGroupChange$: Observable<WidgetLinkingGroupId>;

    public notification$: Observable<WidgetNotification>;

    public title$: Observable<string>;

    /**
     * Allows to transfer any the widget related actions between amount of initiators and executors.
     * @type {Subject<WidgetActionEvent>}
     */
    public readonly action$ = new Subject<WidgetActionEvent>();

    private widgetLinkingGroupService: WidgetLinkingGroupService;

    private groupSubscription: Subscription;

    private destroySubject$ = new AsyncSubject<any>();

    private widgetSettingsToGroupSettingsMap: SettingsMap = {};

    private groupSettingsToWidgetSettingsMap: SettingsMap = {};

    private linkingGroupChangeSubject$ = new Subject<WidgetLinkingGroupId>();

    private readonly titleSubject$ = new ReplaySubject<string>(1);

    private readonly translationService: L10nTranslationService = this.injector.get(L10nTranslationService);

    constructor(
        private injector: Injector
    ) {
        this.destroy$ = this.destroySubject$.asObservable();
        this.linkingGroupChange$ = this.linkingGroupChangeSubject$.asObservable();

        this.destroy$.subscribe(() => this.action$.complete());
    }

    public init(
        widgetDescription: WidgetDescription,
        settings$: Observable<WidgetSettings>,
        tabId: string,
        id?: WidgetId,
        linkingGroupId?: WidgetLinkingGroupId
    ): void {
        this.widgetDescription = widgetDescription;
        this.implementationInstance = Injector
            .create({
                name: `${widgetDescription.widgetTypeId} Implementation Injector`,
                parent: this.injector,
                providers: [{
                    provide: widgetDescription.widgetImplementationType,
                    useClass: widgetDescription.widgetImplementationType,
                    deps: [Injector]
                }]
            })
            .get(widgetDescription.widgetImplementationType);

        this.settingsWithUpdates$ = this.getWidgetSettingsWithUpdatesStream();
        this.notification$ = this.getWidgetNotificationsStream();

        this.id = id || Widget.getNewWidgetId();
        this.tabId = tabId;

        this.listenToDirectSettingsUpdates();
        this.bypassImplEventsToActions();

        if (widgetDescription.hasGroupingSupport) {
            this.widgetLinkingGroupService = this.injector.get(WidgetLinkingGroupService);
            this.createSettingsMap();
            this.linkingGroupId = linkingGroupId || WidgetLinkingGroupId.NONE;
        }

        settings$.pipe(
            switchMap(settings => this.implementationInstance.setSettings(settings)),
            tap(() => this.refreshSubscriptionToGroupSettings()),
            takeUntil(this.destroy$)
        ).subscribe();

        this.initTitleStream();
    }

    public destroy(): void {
        this.unsubscribeFromPreviousGroupSettings();

        const instance = this.implementationInstance;

        if (instance && instance.destroy) {
            instance.destroy();
        }

        this.destroySubject$.next(true);
        this.destroySubject$.complete();
    }

    public setTabId(tabId: string): void {
        this.tabId = tabId;

        this.refreshSubscriptionToGroupSettings();

        this.linkingGroupChangeSubject$.next(this.linkingGroupId);
    }

    public setLinkingGroup(linkingGroupId: WidgetLinkingGroupId): void {
        this.linkingGroupId = linkingGroupId;
        this.refreshSubscriptionToGroupSettings();

        this.linkingGroupChangeSubject$.next(linkingGroupId);
    }

    public getWidgetSizesStandard(): SizesStandard {
        return this.widgetDescription.widgetSizes;
    }

    private createSettingsMap(): void {
        const settingsSchema: WidgetSettingsSchemaField[] = this.widgetDescription.settingsSchema;

        _.forEach(settingsSchema, setting => {
            if (setting.isShareable) {
                const sharedName = setting.sharedName || setting.name;

                this.widgetSettingsToGroupSettingsMap[setting.name] = sharedName;
                this.groupSettingsToWidgetSettingsMap[sharedName] = setting.name;
            }
        });
    }

    private convertSettingsNames(settings: Partial<WidgetSettings>, settingsMap: SettingsMap): Partial<WidgetSettings> {
        const convertedSettings: Partial<WidgetSettings> = {};

        _.forEach(settings, (value, settingName) => {
            const name = settingsMap[settingName] as keyof WidgetSettings;
            if (name) {
                convertedSettings[name] = value;
            }
        });

        return convertedSettings;
    }

    private convertGroupSettingsToWidgetSettings(
        groupSettings: Partial<WidgetSettings>
    ): Partial<WidgetSettings> {
        return this.convertSettingsNames(groupSettings, this.groupSettingsToWidgetSettingsMap);
    }

    private convertWidgetSettingsToGroupSettings(
        widgetSettings: Partial<WidgetSettings>
    ): Partial<WidgetSettings> {
        return this.convertSettingsNames(widgetSettings, this.widgetSettingsToGroupSettingsMap);
    }

    private refreshSubscriptionToGroupSettings(): void {
        this.unsubscribeFromPreviousGroupSettings();

        if (this.linkingGroupId) {
            const groupSettings = this.convertWidgetSettingsToGroupSettings(this.implementationInstance.settings);

            this.groupSubscription = this.widgetLinkingGroupService
                .getLinkingGroupSettingsStream(this.linkingGroupId, this.tabId, groupSettings)
                .subscribe(
                    settings => this.onGroupSettingsChange(settings)
                );
        }
    }

    private unsubscribeFromPreviousGroupSettings(): void {
        if (this.groupSubscription) {
            this.groupSubscription.unsubscribe();
            this.groupSubscription = undefined;
        }
    }

    private onGroupSettingsChange(groupSettings: Partial<WidgetSettings>): void {
        if (!_.isEmpty(groupSettings)) {
            const settings = this.convertGroupSettingsToWidgetSettings(groupSettings);
            const flags = {[GROUP_SETTINGS_UPDATE_FLAG]: true};

            this.implementationInstance
                .updateSettings(settings, flags)
                .pipe(
                    takeUntil(this.destroy$)
                ).subscribe();
        }
    }

    private updateGroupSettings(settings: Partial<WidgetSettings>): void {
        if (this.linkingGroupId) {
            const groupSettings = this.convertWidgetSettingsToGroupSettings(settings);

            if (!_.isEmpty(groupSettings)) {
                this.widgetLinkingGroupService.setLinkingGroupSettings(this.linkingGroupId, this.tabId, groupSettings);
            }
        }
    }

    /**
     * Updates group on the widget's settings change.
     * Ignores changes made by group in order to prevent looping.
     */
    private listenToDirectSettingsUpdates(): void {
        const updates$ = this.implementationInstance.event$.pipe(
            filter((event: WidgetImplementationEvent) =>
                event.type === WidgetImplementationEventType.SETTINGS_UPDATED
                && (!event.flags || !event.flags[GROUP_SETTINGS_UPDATE_FLAG])
            ),
            map(event => event.payload)
        );

        this.implementationInstance.ready$.pipe(
            switchMap(isReady => isReady
                ? updates$
                : NEVER
            )
        ).subscribe(settings => this.updateGroupSettings(settings));
    }

    private getWidgetSettingsWithUpdatesStream(): Observable<Partial<WidgetSettings>> {
        const implSettingsWithUpdates$ = this.implementationInstance.settingsWithUpdates$
            || getWidgetSettingsWithUpdatesStream(this.implementationInstance);

        return implSettingsWithUpdates$.pipe(
            takeUntil(this.destroy$)
        );
    }

    private getWidgetNotificationsStream(): Observable<WidgetNotification> {
        return merge(
            this.settingsWithUpdates$.pipe(
                map(settingsUpdate => ({
                    type: WidgetNotificationType.SETTINGS,
                    widget: this,
                    settingsUpdate
                } as WidgetSettingsNotification))
            ),
            this.linkingGroupChange$.pipe(
                map(linkingGroupId => ({
                    type: WidgetNotificationType.LINKING_GROUP_ID,
                    widget: this,
                    linkingGroupId
                } as WidgetLinkingGroupIdNotification))
            )
        ).pipe(
            takeUntil(this.destroy$)
        );
    }

    private initTitleStream(): void {
        const getTitle = () =>
            this.implementationInstance.settings && this.implementationInstance.settings.title
                || this.translationService.translate(this.widgetDescription.widgetLabel);

        const listenToLiveTitle = () => {
            const titleUpdate$ = this.settingsWithUpdates$.pipe(
                filter(settings => settings.title !== undefined)
            );

            return merge(
                this.translationService.onChange(),
                titleUpdate$
            ).pipe(
                tap(() => this.titleSubject$.next(getTitle())),
                takeUntil(this.destroy$),
                share()
            );
        };

        this.title$ = this.implementationInstance.title$ || this.titleSubject$.pipe(
            dependOn(listenToLiveTitle()),
            takeUntil(this.destroy$)
        );
    }

    /**
     * Allows to pass {@link WidgetActionEvent} from implementation to {@link Widget}.
     */
    private bypassImplEventsToActions(): void {
        this.implementationInstance.event$.pipe(
            tap(event => {
                if (event.type === WidgetImplementationEventType.WIDGET_ACTION) {
                    this.action$.next(event.payload);
                }
            })
        ).subscribe();
    }

}
